import { Preferences } from '@capacitor/preferences';
import { Storage } from '@ionic/storage';
import type { PiniaPluginContext, StateTree } from 'pinia';

// Small in-house replacement for the (now-dropped) pinia-plugin-capacitor-persist
// package: persists a store's whole $state and restores it on startup. Only
// `persist: { enabled: true }` is supported — that's the only shape any
// store in this app actually uses.
export interface PersistOptions {
  enabled: true;
}

declare module 'pinia' {
  // Generic params must match Pinia's own DefineStoreOptionsBase signature
  // for this declaration merge to apply, even though they're unused here.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface DefineStoreOptionsBase<S extends StateTree, Store> {
    persist?: PersistOptions;
  }
}

// Drafts can hold several MB of base64-encoded photos (see CreateFolder) —
// @capacitor/preferences is backed by localStorage on web, whose ~5-10MB
// per-origin quota a couple of in-progress drafts can blow straight through
// (QuotaExceededError, seen in production via Sentry). @ionic/storage is
// IndexedDB-backed with a much larger practical quota, so it's used for the
// actual persistence now; Preferences is only kept around to migrate
// anything already written under the old backend.
let ionicStorage: Storage | undefined;

async function getStorage(): Promise<Storage> {
  if (!ionicStorage) {
    ionicStorage = new Storage();
    await ionicStorage.create();
  }

  return ionicStorage;
}

// One-time move of anything already persisted under the old
// localStorage-backed key, so upgrading to this doesn't drop a user's
// in-progress draft. Safe to call on every startup: a no-op once migrated
// (or if there was never anything to migrate).
async function migrateFromPreferences(key: string, store: Storage): Promise<void> {
  const { value } = await Preferences.get({ key });
  if (!value) {
    return;
  }

  await store.set(key, value);
  await Preferences.remove({ key });
}

export async function piniaCapacitorPersist({
  options,
  store,
}: PiniaPluginContext): Promise<void> {
  if (options.persist?.enabled !== true) {
    return;
  }

  const key = store.$id;
  const storage = await getStorage();

  // A corrupted persisted value (or a failed migration) must not prevent
  // $subscribe from being registered below — otherwise this store would
  // silently stop persisting anything for the rest of the session, not
  // just fail to restore once.
  try {
    await migrateFromPreferences(key, storage);

    const value = await storage.get(key);
    if (value) {
      store.$patch(JSON.parse(value));
    }
  } catch (error) {
    console.warn(`Impossible de restaurer les données persistées pour "${key}"`, error);
  }

  store.$subscribe(() => {
    storage.set(key, JSON.stringify(store.$state)).catch((error) => {
      console.warn(`Impossible de sauvegarder les données persistées pour "${key}"`, error);
    });
  });
}
